<template>
  <v-container v-if="isLoading" fluid fill-width class="ma-0 pa-2">
    <v-row class="ma-0 pa-0">
      <v-col cols="12" class="ma-0 pa-0"> <loading-spinner /> </v-col>
    </v-row>
  </v-container>

  <v-container
    v-else
    fluid
    fill-width
    class="ma-0 pa-0"
    center="start"
    justify="start"
  >
    <v-row class="pa-0 ma-0">
      <v-col cols="12" class="pa-0 ma-0">
        <v-container
          fluid
          fill-width
          class="ma-0 pa-2"
          center="start"
          justify="start"
        >
          <v-app-bar app class="primary">
            <v-icon color="white" x-large>mdi-database-export</v-icon>
            <v-toolbar-title class="white--text pl-3"
              >Dataset Exporter
              <div class="subheading">
                Dataset:
                <v-tooltip bottom color="primary">
                  <template v-slot:activator="{ on: tooltip }">
                    <span v-on="{ ...tooltip }">{{ collection.label }}</span>
                  </template>
                  <span class="subtitle-1">{{
                    getCollectionDescription(collection)
                  }}</span>
                </v-tooltip>
              </div>
            </v-toolbar-title>
            <v-spacer></v-spacer>
          </v-app-bar>

          <v-container fluid fill-width class="ma-0 pa-0 pt-0">
            <!-- select data export type -->
            <v-row class="ma-0 pa-0 pt-2 pb-3">
              <v-col cols="12" class="ma-0 pa-0">
                <v-sheet color="white" height="100%" class="shadow">
                  <v-container fluid fill-width class="ma-0 pa-0">
                    <v-row class="ma-0 pa-0">
                      <v-col cols="12" class="ma-0 pa-0">
                        <v-container fluid fill-width class="ma-0 pa-0">
                          <v-row class="ma-0 pa-0">
                            <v-col cols="12" class="ma-0 pa-0">
                              <v-card color="#eeeeee" class="pt-1">
                                <v-card-title class="primary--text pl-3 py-2">
                                  SELECT DATA EXPORT TYPE
                                </v-card-title>
                              </v-card>
                            </v-col>
                          </v-row>
                        </v-container>

                        <v-select
                          v-model="exportType"
                          label="Export type:"
                          :items="exportTypes"
                          class="ma-3"
                          max-width="80%"
                        ></v-select>
                      </v-col>
                    </v-row>
                  </v-container>
                </v-sheet>
              </v-col>
            </v-row>
            <!-- end - select data export type -->

            <!-- select study groups -->
            <v-row class="ma-0 pa-0 pt-0">
              <v-col cols="12" class="ma-0 pa-0">
                <v-sheet color="white" height="100%" class="shadow">
                  <v-container fluid fill-width class="ma-0 pa-0">
                    <v-row class="ma-0 pa-0">
                      <v-col cols="12" class="ma-0 pa-0">
                        <cohort-table
                          ref="cohortTable"
                          title="SELECT STUDY GROUPS USED TO DEFINE CLASSES"
                          :cohorts="selectedCohorts"
                          :select-cohorts="visibleCohorts"
                          :disable-pagination="true"
                          show-select
                          report-max-selected-overlap
                          checkbox-tooltip="Check to include this study group in the next export."
                          @selectedCohorts="updateVisibleCohorts"
                          @maxSelectedOverlap="maxSelectedOverlap"
                        />
                      </v-col>
                    </v-row>

                    <v-row class="ma-0 pa-0">
                      <v-col cols="12" class="ma-0 pa-0">
                        <v-btn
                          class="primary text--white ma-0 px-2 py-0 mx-3 mb-2"
                          :disabled="
                            !twoOrMoreCohortsSelected || maxOverlap > 0
                          "
                          @click="openDialog()"
                          ><v-icon large class="pr-2" color="white"
                            >mdi-database-export</v-icon
                          >
                          Export data</v-btn
                        >
                      </v-col>
                    </v-row>
                  </v-container>
                </v-sheet>
              </v-col>
            </v-row>
            <!-- end - select study groups -->
          </v-container>
        </v-container>

        <v-dialog v-model="exportDialog" persistent scrollable max-width="50%">
          <v-card style="border: 3px solid #3f51b5;">
            <v-card-title class="ma-0 pa-2 primary">
              <span class="white--text title pl-2"
                >Export {{ exportType }}</span
              >
            </v-card-title>

            <v-divider></v-divider>

            <v-container
              v-if="isExportLoading"
              fluid
              fill-width
              class="ma-0 pa-2"
            >
              <v-row>
                <v-col cols="12">
                  <v-progress-circular
                    v-if="progressValue <= 100 && isExportLoading"
                    class="centerProgressBar"
                    :rotate="-90"
                    :size="100"
                    :width="15"
                    :value="progressValue"
                    :model-value="progressValue"
                    color="primary"
                  >
                    {{ progressValue }}%
                  </v-progress-circular>
                </v-col>
              </v-row>
            </v-container>

            <v-card-text v-else class="pa-3">
              <v-container>
                <v-row class="pa-0 ma-0">
                  <v-col cols="12" class="pa-0 ma-0">
                    <v-text-field
                      v-model="datasetName"
                      label="Dataset name:"
                      class="pa-0 ma-0"
                      :rules="[validateDatasetName]"
                    ></v-text-field>
                  </v-col>
                </v-row>

                <v-row v-for="(vc, vci) in visibleCohorts" :key="`vdr-${vci}`">
                  <v-col cols="12" class="pa-0 ma-0">
                    <v-container class="pa-0 ma-0">
                      <v-row class="py-2 ma-0">
                        <v-col cols="10" class="pa-0 ma-0">
                          <span class="font-weight-bold">Study group:</span>
                          {{ vc.label }}
                        </v-col>
                        <v-col cols="2" class="pa-0 ma-0">
                          <span class="font-weight-bold"
                            >{{ vc.subject_ids.length }} subject(s)</span
                          >
                        </v-col>
                      </v-row>

                      <v-row class="py-2 ma-0">
                        <v-col cols="12" class="pa-0 ma-0">
                          <span class="font-weight-bold">Query:</span>
                          {{ vc.query_string }}
                        </v-col>
                      </v-row>
                      <v-row class="pt-3 ma-0">
                        <v-col cols="12" class="pa-0 ma-0">
                          <v-text-field
                            v-model="visibleCohortNames[vc.id]"
                            label="Enter/Edit Class Name (default is study group name):"
                            :rules="[validateClassName]"
                            dense
                            outlined
                            @update:error="classNameError(vc.id, $event)"
                          ></v-text-field>
                        </v-col>
                      </v-row>
                    </v-container>
                  </v-col>
                </v-row>

                <v-row class="pa-0 ma-0 pt-2">
                  <v-col class="pa-0 ma-0">
                    <v-icon
                      v-if="nSelectedSubjects < nSubjects"
                      class="pa-2"
                      color="warning"
                      medium
                      >warning</v-icon
                    ><v-icon v-else class="pa-2" medium>check</v-icon
                    ><span class="font-italic font-weight-bold"
                      >Exported dataset includes {{ nSelectedSubjects }} /
                      {{ nSubjects }} subject(s).</span
                    >
                  </v-col>
                </v-row>
              </v-container>
            </v-card-text>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn text color="red" @click="exportDialog = false">
                <v-icon left>close</v-icon> Cancel
              </v-btn>

              <v-btn
                class="primary white--text ma-0 px-2 mx-2"
                :disabled="
                  !validateDatasetName(datasetName) ||
                    nValidClassNames != visibleCohorts.length ||
                    isExportLoading
                "
                @click="exportCohortList(visibleCohorts)"
                ><v-icon large class="pr-2" color="white"
                  >mdi-database-export</v-icon
                >EXPORT
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import axios from 'axios';
import { mapActions, mapState } from 'vuex';
import { actions, state } from '@/store/modules/dataExplorer/types';
import CohortTable from '@/components/common/CohortTable.vue';
import { getCollectionDescription } from '@/utils/helpers';
import logEvent from '@/utils/logging';

const EXPORT_TYPES = ['Baseline dataset for classification training.'];
const DATASET_NAME_PATTERN = /^[a-zA-Z0-9_-]{1,50}$/;
const CLASS_NAME_PATTERN = /^[a-zA-Z0-9_-\s\\./]{1,50}$/;

export default {
  name: 'DataExporter',
  components: {
    CohortTable,
  },
  props: {
    collectionId: {
      type: Number,
      required: true,
    },
  },
  data() {
    return {
      isLoading: false,
      isExportLoading: false,
      progressValue: 0,
      getCollectionDescription: getCollectionDescription,
      exportTypes: EXPORT_TYPES,
      exportType: EXPORT_TYPES[0],
      exportDialog: false,
      visibleCohortNames: {},
      validVisibleCohortNames: {},
      maxOverlap: 0,
      nSubjects: 0,
      nSelectedSubjects: 0,
      datasetName: '',
      validateDatasetName: n => DATASET_NAME_PATTERN.test(n),
      validateClassName: n => CLASS_NAME_PATTERN.test(n),
      nValidClassNames: 0,
    };
  },
  computed: {
    ...mapState('dataExplorer', {
      cohorts: state.COHORTS,
      data: state.DATA,
      collection: state.COLLECTION,
      outcomeVariables: state.OUTCOME_VARIABLES,
      visibleCohorts: state.VISIBLE_COHORTS,
    }),
    collectionCohorts() {
      var cc = [];
      var collectionId = this.collection.id;
      this.cohorts.forEach(c => {
        if (c.collection_id == collectionId) {
          cc.unshift(c);
        }
      });
      return cc;
    },
    twoOrMoreCohortsSelected() {
      return this.visibleCohorts.length >= 1;
    },
  },
  watch: {
    visibleCohorts() {
      this.resetClassNames();
      this.countSubjects();
    },
  },
  async created() {
    this.isLoading = true;
    await this.clearData();
    await this.fetchCollection(this.collectionId);
    await this.fetchData();
    await this.fetchCohorts(this.collectionId);

    // determine selected cohorts and assign colors
    var selectedCohorts = this.getCohortsFromIdList(this.cohortIds);
    // if nothing selected then include all cohorts
    if (!selectedCohorts || selectedCohorts.length === 0) {
      selectedCohorts = this.collectionCohorts;
    }
    var scc = [...selectedCohorts].sort((x, y) => y['id'] - x['id']);
    this.selectedCohorts = scc;
    this.isLoading = false;
  },
  methods: {
    ...mapActions('dataExplorer', {
      fetchCohorts: actions.FETCH_COHORTS,
      fetchCollection: actions.FETCH_COLLECTION,
      clearData: actions.CLEAR_DATA,
      fetchData: actions.FETCH_DATA,
      setVisibleCohorts: actions.SET_VISIBLE_COHORTS,
    }),
    updateVisibleCohorts(vc) {
      this.setVisibleCohorts(vc);
    },
    maxSelectedOverlap(mso) {
      this.maxOverlap = mso ? mso['n_in_both'] : 0;
    },
    openDialog() {
      // set default name
      this.datasetName = 'podvis_sd' + this.collection.id;
      this.exportDialog = true;
    },
    resetClassNames() {
      this.visibleCohorts.forEach(c => {
        this.visibleCohortNames[c.id] = c.label;
        this.validVisibleCohortNames[c.id] = true;
      });
      this.nValidClassNames = this.visibleCohorts.length;
    },
    classNameError(id, b) {
      this.validVisibleCohortNames[id] = !b;
      this.countValidClassNames();
    },
    countSubjects() {
      let sel_subj_ids = {};
      this.visibleCohorts.forEach(c => {
        c.subject_ids.forEach(sid => {
          sel_subj_ids[sid] = 1;
        });
      });
      this.nSubjects = this.data.length;
      this.nSelectedSubjects = Object.keys(sel_subj_ids).length;
    },
    countValidClassNames() {
      let nv = 0;
      Object.keys(this.validVisibleCohortNames).forEach(k => {
        if (this.validVisibleCohortNames[k]) nv++;
      });
      this.nValidClassNames = nv;
    },
    getCohortsFromIdList(idList) {
      var sc = [];
      if (typeof idList !== 'undefined' && idList !== '') {
        const cids = {};
        var collectionId = this.collection.id;
        idList.split(',').forEach(c => {
          cids[c] = 1;
        });
        this.cohorts.forEach(c => {
          if (c.collection_id === collectionId && c.id in cids) {
            sc.push(c);
          }
        });
      }
      return sc;
    },
    async exportCohortList(cohorts) {
      this.isExportLoading = true;
      this.setProgressValue(0);
      var dataset_name = this.datasetName;
      var sortedCohorts = [...cohorts].sort((x, y) => y['id'] - x['id']);

      // each cohort becomes a class
      const classes = [];
      sortedCohorts.forEach(c => {
        if (c.collection_id === this.collection.id) {
          classes.push({
            name: this.visibleCohortNames[c.id],
            subject_ids: c.subject_ids,
          });
        }
      });
      // download zipped data file
      try {
        const request_data = {
          dataset_name,
          classes,
        };
        const data_url = `/api/collections/${this.collection.id}/export_data`;
        const response = await axios.post(data_url, request_data, {
          responseType: 'blob',
          onDownloadProgress: progressEvent => {
            const totalLength = progressEvent.lengthComputable
              ? progressEvent.total
              : 100;
            let newProgressValue = Math.round(
              (progressEvent.loaded * 100) / totalLength
            );
            if (newProgressValue - this.progressValue >= 10) {
              this.setProgressValue(newProgressValue);
            }
          },
        });
        let m = response.headers['content-disposition'].match(/filename=(\S+)/);
        const blob = new Blob([response.data], {
          type: response.headers['content-type'],
        });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = m.length == 2 ? m[1] : 'export.zip';
        link.click();
        URL.revokeObjectURL(link.href);
        logEvent(
          this.$gtag,
          null,
          null,
          'data_export',
          'export',
          'collection #' + this.collection.id
        );
        this.setProgressValue(100);
        //alert("Export Successful");
      } catch (err) {
        if (err.response.status == 401) {
          alert('Not authorized to export data');
        } else {
          alert('Error exporting data');
        }
      }
      if (this.progressValue === 100) {
        setTimeout(() => {
          this.exportDialog = false;
          this.isExportLoading = false;
          this.setProgressValue(0);
        }, 500);
      } else {
        this.exportDialog = false;
        this.isExportLoading = false;
        this.setProgressValue(0);
      }
    },
    setProgressValue(v) {
      this.progressValue = v;
    },
  },
};
</script>

<style scoped>
.centerProgressBar {
  margin: auto;
  width: 100%;
  left: 40%;
}
</style>
